import { gql } from '@apollo/client';

/**
 * Triaging issues, and turning them into prompt versions.
 *
 * The three marks are not interchangeable. FIXED says the behaviour was wrong
 * and has been dealt with. WON'T FIX says it was wrong and will be lived with.
 * REVIEWED with `promote: true` says it should teach the agent something —
 * that is what feeds an improvement run, so it is the one that changes future
 * behaviour rather than just closing a row.
 */

export const MARK_ISSUE_FIXED = gql`
  mutation MarkConversationIssueFixed($issueId: Int!, $reviewerNotes: String) {
    markConversationIssueFixed(issueId: $issueId, reviewerNotes: $reviewerNotes) {
      id status reviewed reviewerNotes promotedToLesson
    }
  }
`;

export const MARK_ISSUE_WONT_FIX = gql`
  mutation MarkConversationIssueWontFix($issueId: Int!, $reviewerNotes: String) {
    markConversationIssueWontFix(issueId: $issueId, reviewerNotes: $reviewerNotes) {
      id status reviewed reviewerNotes promotedToLesson
    }
  }
`;

/** Reviewed, and optionally promoted to a lesson the next run will use. */
export const MARK_ISSUE_REVIEWED = gql`
  mutation MarkConversationIssueReviewed($issueId: Int!, $reviewerNotes: String, $promote: Boolean!) {
    markConversationIssueReviewed(issueId: $issueId, reviewerNotes: $reviewerNotes, promote: $promote) {
      id status reviewed reviewerNotes promotedToLesson
    }
  }
`;

/**
 * Run the improver for one agent.
 *
 * `agentName` is the `ImprovableAgentName` ENUM, not a string — which is why
 * app_admin interpolates it into the query text instead of passing a variable.
 * Declared properly here, so a bad name is rejected by the client rather than
 * producing a malformed document.
 */
export const TRIGGER_PROMPT_IMPROVEMENT = gql`
  mutation TriggerPromptImprovement($agentName: ImprovableAgentName!, $force: Boolean!) {
    triggerPromptImprovement(agentName: $agentName, force: $force) {
      id
      agentName
      status
      startedAt
      finishedAt
      issuesUsedCount
      rulesAppliedCount
      promptVersionId
      summary
      error
    }
  }
`;

/** Make a version the live prompt. What the agent answers with from then on. */
export const ACTIVATE_PROMPT_VERSION = gql`
  mutation ActivatePromptVersion($versionId: Int!) {
    activatePromptVersion(versionId: $versionId) {
      id
      agentName
      version
      isActive
      status
      activatedBy
      activatedAt
    }
  }
`;

/**
 * RUN THE ANALYSIS — find out what the agents are getting wrong.
 *
 * This is the detect-and-fix pass: it reads recent conversations together with
 * the agent's current prompt and emits the deduped issues, the consolidated
 * fixes and the section edits, storing the result as a new prompt version.
 *
 * `apply` defaults to FALSE and this app always sends false. Analysis alone
 * never changes a live prompt — the issues and the proposed version are stored
 * so they can be read first, and going live is a separate, deliberate
 * `activatePromptVersion`. Sending true would activate an unvalidated prompt
 * that customers immediately start hearing.
 *
 * `agentName` is the `ImprovableAgentName` enum; leaving it NULL sweeps every
 * online agent with recent activity. `force` skips the gate that requires a
 * minimum number of new conversations since the last pass.
 *
 * Returns immediately with a confirmation — the work happens in the
 * background, so the screen re-reads the runs rather than waiting.
 *
 * It replaces the per-thread `analyzeConversation` app_admin still calls,
 * which no longer exists on the backend.
 */
export const IMPROVE_ONLINE_AGENT = gql`
  mutation ImproveOnlineAgent($agentName: ImprovableAgentName, $force: Boolean!, $apply: Boolean!) {
    improveOnlineAgent(agentName: $agentName, force: $force, apply: $apply)
  }
`;

/**
 * Judge activated-but-unvalidated versions against the conversations they
 * actually produced, keeping what improved the issue rate and rolling back
 * what regressed. Normally scheduled; this forces a pass now. A version that
 * has not soaked yet (30 minutes or 50 conversations) is reported as soaking
 * and left alone.
 */
export const VALIDATE_PROMPT_VERSIONS = gql`
  mutation ValidatePromptVersions { validatePromptVersions }
`;
